import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

const SIGNAL_CATALOG = [
  { signalId: 'otp_request',     tier: 'critical', weight: 35, label: 'OTP / Verification Code Request' },
  { signalId: 'install_app',     tier: 'critical', weight: 35, label: 'Install App / Remote Access' },
  { signalId: 'secrecy',         tier: 'critical', weight: 35, label: 'Asked for Secrecy' },
  { signalId: 'impersonation',   tier: 'critical', weight: 35, label: 'Impersonating Authority' },
  { signalId: 'balance_ask',     tier: 'high',     weight: 20, label: 'Account Balance Inquiry' },
  { signalId: 'be_alone',        tier: 'high',     weight: 20, label: 'Asked Victim to Be Alone' },
  { signalId: 'urgency',         tier: 'high',     weight: 20, label: 'Urgency / Pressure' },
  { signalId: 'birthday_id',     tier: 'medium',   weight: 10, label: 'Birthday / ID Request' },
  { signalId: 'callback',        tier: 'medium',   weight: 10, label: 'Callback Number Provided' },
  { signalId: 'confirm_data',    tier: 'medium',   weight: 10, label: 'Confirming Personal Data' },
  { signalId: 'unknown_num',     tier: 'low',      weight: 5,  label: 'Unknown / VOIP Number' },
  { signalId: 'accent_mismatch', tier: 'low',      weight: 5,  label: 'Accent Mismatch' },
];

const STATUS_CONFIG = {
  confirmed_scam: { label: 'Confirmed Scam',  color: 'error',   icon: ErrorIcon,        gaugeColor: '#d32f2f' },
  high_risk:      { label: 'High Risk',        color: 'warning', icon: WarningIcon,      gaugeColor: '#f57c00' },
  suspicious:     { label: 'Suspicious',       color: 'warning', icon: WarningIcon,      gaugeColor: '#fbc02d' },
  monitoring:     { label: 'Monitoring',       color: 'default', icon: ShieldIcon,       gaugeColor: '#757575' },
  cleared:        { label: 'Cleared',          color: 'success', icon: CheckCircleIcon,  gaugeColor: '#388e3c' },
};

const TIER_COLOR = {
  critical: 'error',
  high:     'warning',
  medium:   'info',
  low:      'default',
};

const formatDateTime = (value) =>
  value ? new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value)) : '-';

function ScoreGauge({ score, status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.monitoring;
  const radius = 54;
  const circumference = Math.PI * radius;
  const fraction = Math.min(Math.max(score, 0), 100) / 100;
  const strokeDashoffset = circumference * (1 - fraction);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
      <Box position="relative" width={140} height={80}>
        <svg width="140" height="80" viewBox="0 0 140 80">
          <path
            d="M 14 70 A 56 56 0 0 1 126 70"
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path
            d="M 14 70 A 56 56 0 0 1 126 70"
            fill="none"
            stroke={cfg.gaugeColor}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <Box
          position="absolute"
          bottom={4}
          left={0}
          right={0}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Typography variant="h4" fontWeight="bold" color={cfg.gaugeColor} lineHeight={1}>
            {score}
          </Typography>
          <Typography variant="caption" color="text.secondary">/ 100</Typography>
        </Box>
      </Box>
      <Chip label={cfg.label} color={cfg.color} size="small" />
    </Box>
  );
}

function SignalBreakdown({ detectedSignals = [] }) {
  const detectedSet = new Set((detectedSignals || []).map((s) => s.signalId));
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Signal</TableCell>
          <TableCell>Tier</TableCell>
          <TableCell align="right">Weight</TableCell>
          <TableCell align="center">Detected</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {SIGNAL_CATALOG.map((sig) => {
          const present = detectedSet.has(sig.signalId);
          return (
            <TableRow key={sig.signalId} sx={{ opacity: present ? 1 : 0.4 }}>
              <TableCell>{sig.label}</TableCell>
              <TableCell>
                <Chip label={sig.tier} color={TIER_COLOR[sig.tier]} size="small" />
              </TableCell>
              <TableCell align="right">+{sig.weight}</TableCell>
              <TableCell align="center">{present ? '✓' : ''}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function CallTimeline({ timeline = [] }) {
  if (timeline.length === 0) {
    return <Typography variant="body2" color="text.secondary">No calls recorded yet.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {timeline.map((call) => (
        <Paper key={call.scammerCallId} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {formatDateTime(call.callTime)}
              </Typography>
              {call.callId && (
                <Typography variant="caption" color="text.secondary">
                  Call ID: {call.callId}
                </Typography>
              )}
            </Box>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(call.signals || []).map((s) => (
                <Tooltip key={s.signalId} title={`+${s.weight} pts`}>
                  <Chip
                    label={s.signalId.replace(/_/g, ' ')}
                    color={TIER_COLOR[s.tier] || 'default'}
                    size="small"
                  />
                </Tooltip>
              ))}
              {(!call.signals || call.signals.length === 0) && (
                <Typography variant="caption" color="text.secondary">no signals</Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function PatternDetail({ callerId, victimId, onClose }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(
    ['patternMemoryEntry', callerId, victimId],
    () => api.getPatternMemoryEntry(callerId, victimId),
    { retry: false }
  );

  const mutation = useMutation(
    (newStatus) => api.setPatternMemoryStatus(callerId, victimId, newStatus),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['patternMemoryEntry', callerId, victimId]);
        queryClient.invalidateQueries(['patternMemoryByCaller', callerId]);
        queryClient.invalidateQueries(['patternMemoryByVictim', victimId]);
      },
    }
  );

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">Failed to load detail: {error.message}</Alert>;
  if (!data) return null;

  const cfg = STATUS_CONFIG[data.status] || STATUS_CONFIG.monitoring;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {callerId} → {victimId}
        </Typography>
        {onClose && (
          <Button size="small" onClick={onClose}>Back</Button>
        )}
      </Stack>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm="auto">
          <ScoreGauge score={data.cumulativeScore} status={data.status} />
        </Grid>
        <Grid item xs={12} sm>
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">Calls</Typography>
              <Typography variant="h6">{data.callCount}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">First contact</Typography>
              <Typography variant="body2">{formatDateTime(data.firstContact)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Last contact</Typography>
              <Typography variant="body2">{formatDateTime(data.lastContact)}</Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm="auto">
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">Override status</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={data.status}
                onChange={(e) => mutation.mutate(e.target.value)}
                disabled={mutation.isLoading}
              >
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <MenuItem key={key} value={key}>{val.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {mutation.isError && (
              <Typography variant="caption" color="error">{mutation.error.message}</Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>Signal Breakdown</Typography>
      <SignalBreakdown detectedSignals={data.detectedSignals} />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>Call Timeline</Typography>
      <CallTimeline timeline={data.timeline} />
    </Box>
  );
}

function ResultList({ label, entries, onSelect }) {
  if (!entries || entries.length === 0) {
    return <Typography variant="body2" color="text.secondary">No pattern memory found.</Typography>;
  }
  return (
    <Stack spacing={1}>
      {entries.map((entry) => {
        const cfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.monitoring;
        return (
          <Paper
            key={`${entry.callerId}-${entry.victimId}`}
            variant="outlined"
            sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            onClick={() => onSelect(entry)}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {label === 'caller' ? `Victim: ${entry.victimId}` : `Caller: ${entry.callerId}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {entry.callCount} call{entry.callCount !== 1 ? 's' : ''} · Last: {formatDateTime(entry.lastContact)}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight="bold">{entry.cumulativeScore}</Typography>
                <Chip label={cfg.label} color={cfg.color} size="small" />
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

export default function RiskMeter() {
  const [searchMode, setSearchMode] = useState('caller');
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selected, setSelected] = useState(null);

  const { data, isLoading, error, refetch } = useQuery(
    searchMode === 'caller'
      ? ['patternMemoryByCaller', searchValue]
      : ['patternMemoryByVictim', searchValue],
    () => searchMode === 'caller'
      ? api.getPatternMemoryByCaller(searchValue)
      : api.getPatternMemoryByVictim(searchValue),
    { enabled: searchValue.length > 0, retry: false }
  );

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    setSelected(null);
    setSearchValue(trimmed);
  };

  const entries = searchMode === 'caller' ? data?.entries : data?.entries;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        ScamGuard Risk Meter
      </Typography>

      {selected ? (
        <Card>
          <CardContent>
            <PatternDetail
              callerId={selected.callerId}
              victimId={selected.victimId}
              onClose={() => setSelected(null)}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <form onSubmit={handleSearch}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Search by</InputLabel>
                  <Select
                    value={searchMode}
                    label="Search by"
                    onChange={(e) => { setSearchMode(e.target.value); setSearchValue(''); setSelected(null); }}
                  >
                    <MenuItem value="caller">Caller (attacker)</MenuItem>
                    <MenuItem value="victim">Victim (callee)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={searchMode === 'caller' ? 'Caller number' : 'Victim number'}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchIcon />}
                  disabled={!searchInput.trim()}
                >
                  Search
                </Button>
              </Stack>
            </form>
          </Paper>

          {isLoading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.response?.status === 404 ? 'No records found.' : error.message}
            </Alert>
          )}
          {!isLoading && !error && searchValue && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Results for {searchValue}
                </Typography>
                <ResultList
                  label={searchMode}
                  entries={entries}
                  onSelect={(entry) => setSelected(entry)}
                />
              </CardContent>
            </Card>
          )}
          {!searchValue && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <ShieldIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Enter a caller or victim number to view cross-call pattern risk.
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
